import { useEffect, type ReactNode } from 'react'
import { Container, Text, ThemeProvider, usePathname, onLinkClick } from '@cottonray/ui-web'
import type { AccentName, ThemeName } from '@cottonray/design-tokens'
import { AccountPage } from './Account'
import { accountContext } from './account/params'
import { FieldDocs } from './FieldDocs'
import { Home, SiteFooter, SiteHeader } from './Home'
import { PolicyDoc, PrivacyIndex } from './Privacy'
import { CompletionReport } from './CompletionReport'
import { QuoteBuilder } from './QuoteBuilder'
import { ResignationTimeline } from './ResignationTimeline'
import { ServicesPage } from './Services'
import { BRAND } from './content'
import { EasyAirScreenSupport } from './Support'
import { SupportInquiry } from './SupportInquiry'
import { supportContext } from './support/params'
import { findPolicy } from './policies'
import { siteStylesheet } from './styles'
import { studioStylesheet } from './studioStyles'
import { isPlayableSlug, PlayPage } from './Play'

const PRIVACY_ROOT = '/privacy'
const SUPPORT_ROOT = '/support'
// 공용 "내 계정" — 문의 화면과 같은 규칙이다: 페이지는 하나고 서비스는 `?service=` 값이다
const ACCOUNT_ROOT = '/account'

export function App() {
  const pathname = usePathname()
  const page = resolve(pathname)

  useEffect(() => {
    document.title = page.title
  }, [page.title])

  return (
    <ThemeProvider {...(page.theme ?? {})}>
      <style>{siteStylesheet()}</style>
      <style>{studioStylesheet}</style>
      {/* 방침 문서는 사이트 크롬 없이 단독으로 뜬다 — 이유는 Privacy.tsx 주석 */}
      {page.kind === 'bare' ? (
        page.node
      ) : (
        <div className={page.chromeClass === undefined ? 'site' : `site ${page.chromeClass}`}>
          <SiteHeader />
          <main>{page.node}</main>
          <SiteFooter />
        </div>
      )}
    </ThemeProvider>
  )
}

interface Page {
  kind: 'site' | 'bare'
  title: string
  node: ReactNode
  /**
   * 사이트 크롬까지 이 페이지의 테마를 따라가게 한다. 현장서류처럼 다크 시안을 쓰는 페이지에서
   * 헤더·푸터만 밝게 남으면 이음매가 그대로 보인다 — 색 변수를 `.site` 에 얹어 상속시킨다.
   */
  chromeClass?: string
  /**
   * 이 페이지만의 테마. 문의 화면이 **앱이 넘긴 색**으로 떠야 해서 생겼다 —
   * 앱과 같은 토큰 이름(`accent="rose"`)을 받으므로 값은 여전히 한 곳에서 나온다.
   */
  theme?: { name?: ThemeName; accent?: AccentName }
}

/** usePathname은 끝 슬래시를 지워서 주므로 `/privacy/`와 `/privacy`가 같은 값으로 들어온다 */
function resolve(pathname: string): Page {
  if (pathname.startsWith('/play/')) {
    const slug = pathname.slice('/play/'.length)
    if (isPlayableSlug(slug)) return { kind: 'site', title: `Play — ${BRAND}`, node: <PlayPage key={slug} slug={slug} />, chromeClass: 'site--studio', theme: { name: 'dark' } }
  }
  if (pathname === '/') {
    return { kind: 'site', title: `${BRAND} — Small studio. Big play.`, node: <Home />, chromeClass: 'site--studio', theme: { name: 'dark' } }
  }

  // 앱들의 설정 화면 "더 많은 앱"이 이 주소로 들어온다 — 지우면 앱 안 링크가 깨진다
  if (pathname === '/services') {
    return { kind: 'site', title: `Apps — ${BRAND}`, node: <ServicesPage /> }
  }

  if (pathname === PRIVACY_ROOT) {
    return { kind: 'site', title: 'Privacy policies — cottonray', node: <PrivacyIndex /> }
  }

  if (pathname.startsWith(`${PRIVACY_ROOT}/`)) {
    const slug = pathname.slice(PRIVACY_ROOT.length + 1)
    const policy = findPolicy(slug)
    if (policy !== undefined) {
      return { kind: 'bare', title: policy.title, node: <PolicyDoc policy={policy} /> }
    }
  }

  // 현장서류 가격 검증 랜딩. 첫 실험 동안 홈의 서비스 목록에는 노출하지 않는다 —
  // 초대 링크로 들어온 사람만 세야 유효 방문의 분모가 오염되지 않는다
  // (docs/experiments/field-docs/validation-2026-08.md)
  if (pathname === '/field-docs') {
    return {
      kind: 'site',
      title: 'Field Docs — take the photos, get the quote and the completion report',
      node: <FieldDocs />,
      chromeClass: 'site--fd',
    }
  }

  // 견적서 만들기 — 유입 엔진 (docs/goals §4-b). 무료로 값을 주고 사진 자동화만 유료다.
  // 퇴사 타임라인과 같은 부류라 sitemap 에 넣는다 (seo.ts)
  if (pathname === '/quote-builder') {
    return {
      kind: 'site',
      title: '견적서 만들기 — 부가세까지 계산되는 무료 견적서 양식 (가입 없음)',
      node: <QuoteBuilder kind="quote" />,
    }
  }

  // 종류마다 URL 을 따로 둔다 — 검색이 문서 이름으로 일어나기 때문이다(quoteRules DocKind 주석)
  if (pathname === '/statement-builder') {
    return {
      kind: 'site',
      title: '거래명세서 만들기 — 무료 거래명세서 양식, 부가세 자동 계산 (가입 없음)',
      node: <QuoteBuilder kind="statement" />,
    }
  }

  if (pathname === '/completion-report') {
    return {
      kind: 'site',
      title: '작업완료보고서 만들기 — 무료 양식, 인쇄·PDF 저장 (가입 없음)',
      node: <CompletionReport />,
    }
  }

  if (pathname === '/receipt-builder') {
    return {
      kind: 'site',
      title: '간이영수증 만들기 — 무료 영수증 양식, 인쇄·PDF 저장 (가입 없음)',
      node: <QuoteBuilder kind="receipt" />,
    }
  }

  // 퇴사 타임라인 — 수익 라인이 아니라 트래픽 자산 (docs/ideas-2026-08/plans/resignation-timeline.md).
  // field-docs 와 달리 검색 유입이 목적이므로 sitemap 에 넣는다 (seo.ts)
  if (pathname === '/resignation-timeline') {
    return {
      kind: 'site',
      title: 'Resignation Timeline — deadlines for unemployment benefit, health insurance, and severance',
      node: <ResignationTimeline />,
    }
  }


  // 공용 지원 문의 — **전 서비스가 이 한 주소를 연다** (2026-08-26, docs/support.md).
  // 어느 앱의 문의인지는 경로가 아니라 `?service=` 로 온다: 페이지는 하나고 서비스는 값이다
  if (pathname === SUPPORT_ROOT) {
    const context = supportContext()
    return {
      kind: 'bare',
      title: 'Support — cottonray',
      node: <SupportInquiry context={context} />,
      theme: {
        ...(context.theme === undefined ? {} : { name: context.theme }),
        ...(context.accent === undefined ? {} : { accent: context.accent }),
      },
    }
  }

  // 공용 "내 계정" — **전 앱이 이 한 주소를 연다** (2026-08-26, docs/account.md).
  // 앱 안 웹뷰 전용이라 사이트 크롬 없이 뜨고, 세션은 앱이 들고 있다 (Account.tsx)
  if (pathname === ACCOUNT_ROOT) {
    const context = accountContext()
    return {
      kind: 'bare',
      title: 'Account — cottonray',
      node: <AccountPage context={context} />,
      theme: {
        ...(context.theme === undefined ? {} : { name: context.theme }),
        ...(context.accent === undefined ? {} : { accent: context.accent }),
      },
    }
  }

  if (pathname === `${SUPPORT_ROOT}/easy-air-screen`) {
    return {
      kind: 'site',
      title: 'Easy Air Screen support — cottonray',
      node: <EasyAirScreenSupport />,
    }
  }

  return { kind: 'site', title: 'Page not found — cottonray', node: <NotFound /> }
}

function NotFound() {
  return (
    <section className="band">
      <Container>
        <Text variant="title">Page not found</Text>
        <Text tone="muted" className="section__head">
          The address may have changed, or this page never existed.
        </Text>
        <a href="/" onClick={onLinkClick}>
          Back to home
        </a>
      </Container>
    </section>
  )
}
